/**
 * Google Analytics Provider
 * Wrapper around nuxt-gtag
 */

import type { AnalyticsProvider } from './base.provider'
import { AnalyticsEvents } from '../types/events'

// GA event name mapping - ONLY events GA cares about
const GA_EVENT_MAP: Record<string, string> = {
  // Auth events
  [AnalyticsEvents.LOGIN_SUCCESS]: 'login',
  [AnalyticsEvents.LOGOUT]: 'logout_event',
  [AnalyticsEvents.SIGNUP_COMPLETED]: 'sign_up',

  // Content events
  [AnalyticsEvents.POST_CREATED]: 'post_created_event',
  [AnalyticsEvents.POST_EDITED]: 'post_edited_event',
  [AnalyticsEvents.POST_DELETED]: 'post_deleted_event',
  [AnalyticsEvents.COMMENT_CREATED]: 'comment_created_event',
  [AnalyticsEvents.COMMENT_EDITED]: 'comment_edited_event',
  [AnalyticsEvents.COMMENT_DELETED]: 'comment_deleted_event',
  [AnalyticsEvents.REACTION_ADDED]: 'create_reactions_event',
  [AnalyticsEvents.REACTION_REMOVED]: 'delete_reactions_event',

  // Community events
  [AnalyticsEvents.GROUP_JOINED]: 'group_joined_event',
  [AnalyticsEvents.GROUP_CREATED]: 'group_created_event',
  [AnalyticsEvents.GROUP_VIEWED]: 'group_viewed_event',
  [AnalyticsEvents.GROUP_LEFT]: 'group_left_event',
  [AnalyticsEvents.CHANNEL_CREATED]: 'channel_created_event',
  [AnalyticsEvents.CHANNEL_VIEWED]: 'channel_viewed_event',
  [AnalyticsEvents.CHANNEL_JOINED]: 'channel_joined_event',

  // Course events
  [AnalyticsEvents.COURSE_VIEWED]: 'course_viewed_event',
  [AnalyticsEvents.LESSON_COMPLETED]: 'lesson_completed_event',

  // Calendar events
  [AnalyticsEvents.EVENT_CREATED]: 'event_created_event',
  [AnalyticsEvents.EVENT_RSVP_YES]: 'event_rsvp_yes',

  // Media events
  [AnalyticsEvents.MEDIA_UPLOADED]: 'media_uploaded_event',

  // Profile events
  [AnalyticsEvents.PROFILE_VIEWED]: 'profile_viewed',
  [AnalyticsEvents.PROFILE_EDITED]: 'profile_edited',

  // Search events
  [AnalyticsEvents.SEARCH_PERFORMED]: 'search_performed',
  [AnalyticsEvents.SEARCH_RESULT_CLICKED]: 'search_result_clicked',

  // UI events
  [AnalyticsEvents.PAGE_VIEW]: 'page_view',
  [AnalyticsEvents.ELEMENT_CLICKED]: 'element_clicked',
  [AnalyticsEvents.ERROR_OCCURRED]: 'error_occurred',
}

export class GoogleAnalyticsProvider implements AnalyticsProvider {
  name = 'GoogleAnalytics'
  private gtag: any = null
  private debug = false

  constructor(debug = false) {
    this.debug = debug
  }

  setGtag(gtag: any): void {
    this.gtag = gtag
    if (this.debug) {
      console.log('[GoogleAnalytics] gtag instance set')
    }
  }

  track(event: string, properties?: Record<string, any>): void {
    if (!this.gtag) {
      if (this.debug) {
        console.warn('[GoogleAnalytics] gtag not available')
      }
      return
    }

    // Only track events we have mapping for
    const gaEventName = GA_EVENT_MAP[event]
    if (!gaEventName) {
      return // Not our event
    }

    try {
      // Transform properties to GA4 format
      // Exclude userId and contactId to avoid duplicates since we map them to user_id
      const { userId, contactId, ...rest } = properties || {}
      
      // Convert camelCase to snake_case for GA4 standard parameters
      const gaProperties = this.convertToSnakeCase({
        ...rest,
        user_id: userId || contactId,
      })

      this.gtag('event', gaEventName, gaProperties)
      if (this.debug) {
        console.log('[GoogleAnalytics] Event tracked:', gaEventName, gaProperties)
      }
    } catch (error) {
      console.error('[GoogleAnalytics] Error:', error)
    }
  }

  /**
   * Convert camelCase keys to snake_case for GA4 compatibility
   */
  private convertToSnakeCase(obj: Record<string, any>): Record<string, any> {
    const result: Record<string, any> = {}
    
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        // Convert camelCase to snake_case
        const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
        result[snakeKey] = obj[key]
      }
    }
    
    return result
  }

  identify(userId: string, traits?: Record<string, any>): void {
    if (!this.gtag) return

    try {
      const gaUserProperties: Record<string, any> = {}

      if (traits?.contactId) {
        gaUserProperties.contact_id = traits.contactId
      }
      if (traits?.email) {
        gaUserProperties.email = traits.email
      }
      if (traits?.locationId) {
        gaUserProperties.location_id = traits.locationId
      }
      if (traits?.appName) {
        gaUserProperties.app_name = traits.appName
      }

      this.gtag('set', {
        user_id: userId,
        user_properties: gaUserProperties,
      })

      if (this.debug) {
        console.log('[GoogleAnalytics] User identified:', userId, gaUserProperties)
      }
    } catch (error) {
      console.error('[GoogleAnalytics] Identify error:', error)
    }
  }
}



